/**
 * AssistantProvider — Superadmin 전역 AI Assistant Context
 *
 * 역할:
 *  - settings: enabled / model preset / tone / daily budget
 *  - spent: 클라이언트 쪽 누적 사용량 (서버 ai_review_log sum과 주기적 sync)
 *  - invoke 시 budget-guard로 차단 여부 판정
 *
 * 사양: docs/specs/superadmin.md §4.3 (Assistant Settings)
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { checkBudget, type BudgetState } from './budget-guard';

export type AssistantModelPreset = 'auto' | 'sonnet' | 'haiku';
export type AssistantTone = 'concise' | 'detailed' | 'friendly';

export interface AssistantSettings {
  enabled: boolean;
  model: AssistantModelPreset;
  tone: AssistantTone;
  dailyBudgetUsd: number;
  /** 위치별 on/off (Dashboard 브리핑만, Reviewer만 등) */
  features: {
    dashboardBrief: boolean;
    promptReview: boolean;
    rolloutRecommend: boolean;
    feedbackDraft: boolean;
    incidentClassify: boolean;
    shadowComparison: boolean;
    goldenFailureAnalysis: boolean;
    anomalyDetect: boolean;
  };
}

export const DEFAULT_ASSISTANT_SETTINGS: AssistantSettings = {
  enabled: true,
  model: 'auto',
  tone: 'concise',
  dailyBudgetUsd: 15,
  features: {
    dashboardBrief: true,
    promptReview: true,
    rolloutRecommend: true,
    feedbackDraft: true,
    incidentClassify: true,
    shadowComparison: true,
    goldenFailureAnalysis: true,
    anomalyDetect: true,
  },
};

interface AssistantContextValue {
  settings: AssistantSettings;
  updateSettings: (patch: Partial<AssistantSettings>) => void;
  spentUsd: number;
  recordSpend: (usd: number) => void;
  resetSpend: () => void;
  syncSpend: (totalUsd: number) => void;
  budget: BudgetState;
}

const AssistantContext = createContext<AssistantContextValue | null>(null);

const STORAGE_KEY = 'lashpic-assistant-settings-v1';

function loadInitialSettings(initial?: Partial<AssistantSettings>): AssistantSettings {
  if (typeof window === 'undefined') {
    return { ...DEFAULT_ASSISTANT_SETTINGS, ...initial };
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_ASSISTANT_SETTINGS, ...initial };
    const parsed = JSON.parse(raw) as Partial<AssistantSettings>;
    return {
      ...DEFAULT_ASSISTANT_SETTINGS,
      ...parsed,
      ...initial,
      features: { ...DEFAULT_ASSISTANT_SETTINGS.features, ...parsed.features, ...initial?.features },
    };
  } catch {
    return { ...DEFAULT_ASSISTANT_SETTINGS, ...initial };
  }
}

export interface AssistantProviderProps {
  children: ReactNode;
  initialSettings?: Partial<AssistantSettings>;
}

export function AssistantProvider({ children, initialSettings }: AssistantProviderProps) {
  const [settings, setSettings] = useState<AssistantSettings>(() => loadInitialSettings(initialSettings));
  const [spentUsd, setSpentUsd] = useState(0);

  const updateSettings = useCallback((patch: Partial<AssistantSettings>) => {
    setSettings((prev) => {
      const next = {
        ...prev,
        ...patch,
        features: { ...prev.features, ...patch.features },
      };
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* quota exceeded or privacy mode — ignore */
      }
      return next;
    });
  }, []);

  const recordSpend = useCallback((usd: number) => {
    setSpentUsd((prev) => prev + (Number.isFinite(usd) && usd > 0 ? usd : 0));
  }, []);

  const resetSpend = useCallback(() => setSpentUsd(0), []);
  const syncSpend = useCallback((totalUsd: number) => {
    if (Number.isFinite(totalUsd) && totalUsd >= 0) setSpentUsd(totalUsd);
  }, []);

  // 자정 경계에 spent 리셋 (KST). 24h 후 타이머 재설정.
  useEffect(() => {
    const now = new Date();
    const midnight = new Date(now);
    midnight.setHours(24, 0, 0, 0);
    const delay = midnight.getTime() - now.getTime();
    const t = setTimeout(() => setSpentUsd(0), delay);
    return () => clearTimeout(t);
  }, [spentUsd]);

  const budget = useMemo(() => checkBudget(spentUsd, settings.dailyBudgetUsd), [spentUsd, settings.dailyBudgetUsd]);

  const value = useMemo<AssistantContextValue>(
    () => ({
      settings,
      updateSettings,
      spentUsd,
      recordSpend,
      resetSpend,
      syncSpend,
      budget,
    }),
    [settings, updateSettings, spentUsd, recordSpend, resetSpend, syncSpend, budget],
  );

  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>;
}

export function useAssistantContext(): AssistantContextValue {
  const ctx = useContext(AssistantContext);
  if (!ctx) throw new Error('useAssistantContext must be used within <AssistantProvider>');
  return ctx;
}
